import * as React from 'react';
import {AutoForm} from '@soap/baseweb';
import {LinkButton} from "../../z40-uicomp/buttons";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { OfficeInfo } from "./OfficeInfo";

export function ContactForm() {
    const viewStates = {
        showForm: 0,
        showThankYou: 1
    };

    const [viewState, setView] = React.useState(viewStates.showForm);
    const [supportEmailId, setSupportEmailId] = React.useState<string | null>(null);
    const emailValueRef = React.useRef<string | null>(null);
    const formContainerRef = React.useRef<HTMLDivElement>(null);

    function generateSupportEmailId(email: string): string {
        const epochTime = Math.floor(Date.now() / 1000);
        const firstChar = email[0];
        const lastChar = email[email.length - 1];
        const atIndex = email.indexOf('@');
        const lastCharBeforeAt = email[atIndex - 1];
        const firstCharAfterAt = email[atIndex + 1];
        return `${epochTime}${firstChar}${lastChar}${lastCharBeforeAt}${firstCharAfterAt}`;
    }

    // Track email input value
    React.useEffect(() => {
        if (viewState !== viewStates.showForm || !formContainerRef.current) return;

        let cleanup: (() => void) | undefined;

        const checkForEmailInput = () => {
            // Note: AutoForm generates lowercase input names
            const emailInput = formContainerRef.current?.querySelector('input[name="c117_Email"]') as HTMLInputElement;
            if (emailInput && !cleanup) {
                emailValueRef.current = emailInput.value || null;
                const handleInput = () => {
                    emailValueRef.current = emailInput.value || null;
                };
                emailInput.addEventListener('input', handleInput);
                cleanup = () => emailInput.removeEventListener('input', handleInput);
            }
        };

        // Check immediately
        checkForEmailInput();

        // Also watch for when form is rendered
        const observer = new MutationObserver(checkForEmailInput);
        observer.observe(formContainerRef.current, { childList: true, subtree: true });

        return () => {
            observer.disconnect();
            if (cleanup) cleanup();
        };
    }, [viewState]);

    switch (viewState) {
        case viewStates.showForm:
            return (
                <Card className="border-[#1c5375] border-2 shadow-lg hover:shadow-xl transition-shadow">
                    <CardHeader>
                        <CardTitle className="text-[#1c5375] text-2xl md:text-3xl flex items-center gap-3">
                            ✉️ Send us a Message
                        </CardTitle>
                        <CardDescription className="text-[#656565] text-lg">
                            Fill out the form below and we'll get back to you as soon as possible.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div ref={formContainerRef}>
                            <AutoForm
                                query={{
                                    $type: "C118v1_GetC117FormData"
                                }}
                                submitText="Send"
                                afterSubmit={() => {
                                    // Generate ID for display using the tracked email value
                                    let emailValue = emailValueRef.current;
                                    
                                    // Fallback: try to get email from the form directly if tracking failed
                                    if (!emailValue) {
                                        const emailInput = formContainerRef.current?.querySelector('input[name="c117_Email"]') as HTMLInputElement;
                                        emailValue = emailInput?.value || null;
                                    }
                                    
                                    if (emailValue) {
                                        const generatedId = generateSupportEmailId(emailValue);
                                        setSupportEmailId(generatedId);
                                    }
                                    
                                    setView(viewStates.showThankYou);
                                }}
                            />
                        </div>
                        <OfficeInfo embedded />
                    </CardContent>
                </Card>
            );
        case viewStates.showThankYou:
            return (
                <Card className="border-[#7bc8d7] border-2 shadow-lg text-center">
                    <CardHeader>
                        <CardTitle className="text-[#1c5375] text-2xl sm:text-3xl md:text-4xl flex items-center justify-center gap-2 sm:gap-3">
                            ✅ Thank you!
                        </CardTitle>
                        <CardDescription className="text-[#656565] text-lg sm:text-xl">
                            We will respond shortly.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {supportEmailId && (
                            <div className="bg-[#f0f9ff] border border-[#7bc8d7] rounded-lg p-4">
                                <p className="text-[#1c5375] text-sm font-semibold mb-1">Your message ID:</p>
                                <p className="text-[#1c5375] font-mono text-lg font-bold">{supportEmailId}</p>
                            </div>
                        )}
                        <LinkButton
                            onClick={() => {
                                setView(viewStates.showForm);
                                setSupportEmailId(null);
                            }}
                            className="bg-[#1c5375] hover:bg-[#0f3a52] text-white px-6 py-3 rounded-lg text-lg transition-colors"
                        >
                            Send Another Message
                        </LinkButton>
                    </CardContent>
                </Card>
            );
    }
}
